import type { ElementType, ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * The dashboard's card surface: flat fill, hairline border, sharp corners.
 *
 * Elevation is expressed as surface tint + border weight, never a drop
 * shadow — the brand rule, and the reason no variant here accepts one.
 */
export function Panel({
  children,
  /**
   * Border treatment. `accent` variants tint the boundary to signal state
   * (pink = open actions, green = roadmap) exactly as the reference does.
   */
  tone = 'default',
  /** 3px grade-gradient rule along the top edge. */
  gradientRule = false,
  as: Tag = 'section',
  className,
  ...rest
}: {
  children: ReactNode
  tone?: 'default' | 'action' | 'positive' | 'raised'
  gradientRule?: boolean
  as?: ElementType
  className?: string
} & Record<string, unknown>) {
  const toneClass = {
    default: 'border-hairline',
    raised: 'bg-surface-raised border-hairline-strong',
    action: 'border-[color-mix(in_srgb,var(--cta)_45%,transparent)]',
    positive: 'border-[color-mix(in_srgb,var(--growth)_35%,transparent)]',
  }[tone]

  return (
    <Tag
      className={cn('bg-surface-card border', toneClass, className)}
      {...rest}
    >
      {gradientRule && (
        <div
          aria-hidden='true'
          className='h-[3px] w-full'
          style={{ background: 'var(--pm-grade-gradient)' }}
        />
      )}
      {children}
    </Tag>
  )
}
